import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Eye, EyeOff, Pencil } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const emptyForm = {
  name_fr: "", name_en: "",
  region: "", flag_emoji: "", image_url: "",
  summary_fr: "", summary_en: "",
  widows_supported: 0, orphans_supported: 0, programs: 0,
  order: 100, published: true,
};

const ImpactMapManager = ({ tAdmin }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/impact-countries");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      widows_supported: parseInt(form.widows_supported, 10) || 0,
      orphans_supported: parseInt(form.orphans_supported, 10) || 0,
      programs: parseInt(form.programs, 10) || 0,
      order: parseInt(form.order, 10) || 0,
    };
    try {
      if (editId) {
        await api.put(`/admin/impact-countries/${editId}`, payload);
        toast.success(tAdmin.impactMap.updated);
      } else {
        await api.post("/admin/impact-countries", payload);
        toast.success(tAdmin.impactMap.created);
      }
      setForm(emptyForm);
      setShowForm(false);
      setEditId(null);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({
      name_fr: c.name_fr || "", name_en: c.name_en || "",
      region: c.region || "", flag_emoji: c.flag_emoji || "", image_url: c.image_url || "",
      summary_fr: c.summary_fr || "", summary_en: c.summary_en || "",
      widows_supported: c.widows_supported || 0, orphans_supported: c.orphans_supported || 0, programs: c.programs || 0,
      order: c.order || 100, published: !!c.published,
    });
    setShowForm(true);
  };

  const togglePublish = async (c) => {
    try {
      await api.put(`/admin/impact-countries/${c.id}`, { published: !c.published });
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(tAdmin.impactMap.confirmDelete.replace("{name}", c.name_fr))) return;
    try {
      await api.delete(`/admin/impact-countries/${c.id}`);
      toast.success(tAdmin.impactMap.deleted);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="impact-map-manager">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-[#002B49] font-semibold">{tAdmin.impactMap.title}</h2>
          <p className="text-sm text-[#64748B]">{tAdmin.impactMap.subtitle}</p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setEditId(null); setForm(emptyForm); setShowForm(true); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#002B49] text-white hover:bg-[#001F35] text-sm font-medium"
            data-testid="country-add-btn"
          >
            <Plus size={14} /> {tAdmin.impactMap.addCountry}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white rounded-2xl border border-black/5 p-6 mb-6" data-testid="country-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={tAdmin.impactMap.fNameFr}><input required value={form.name_fr} onChange={e => setForm({...form, name_fr: e.target.value})} className="form-input" /></Field>
            <Field label={tAdmin.impactMap.fNameEn}><input required value={form.name_en} onChange={e => setForm({...form, name_en: e.target.value})} className="form-input" /></Field>
            <Field label={tAdmin.impactMap.fRegion}><input required value={form.region} onChange={e => setForm({...form, region: e.target.value})} className="form-input" /></Field>
            <Field label={tAdmin.impactMap.fFlag}><input value={form.flag_emoji} onChange={e => setForm({...form, flag_emoji: e.target.value})} className="form-input" placeholder="🇲🇱" /></Field>
            <Field label={tAdmin.impactMap.fImage} className="sm:col-span-2"><input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} className="form-input" /></Field>
            <Field label={tAdmin.impactMap.fSummaryFr} className="sm:col-span-2"><textarea required rows="3" value={form.summary_fr} onChange={e => setForm({...form, summary_fr: e.target.value})} className="form-input" /></Field>
            <Field label={tAdmin.impactMap.fSummaryEn} className="sm:col-span-2"><textarea required rows="3" value={form.summary_en} onChange={e => setForm({...form, summary_en: e.target.value})} className="form-input" /></Field>
            <Field label={tAdmin.impactMap.fWidows}><input type="number" min="0" value={form.widows_supported} onChange={e => setForm({...form, widows_supported: e.target.value})} className="form-input" /></Field>
            <Field label={tAdmin.impactMap.fOrphans}><input type="number" min="0" value={form.orphans_supported} onChange={e => setForm({...form, orphans_supported: e.target.value})} className="form-input" /></Field>
            <Field label={tAdmin.impactMap.fPrograms}><input type="number" min="0" value={form.programs} onChange={e => setForm({...form, programs: e.target.value})} className="form-input" /></Field>
            <Field label={tAdmin.impactMap.fOrder}><input type="number" value={form.order} onChange={e => setForm({...form, order: e.target.value})} className="form-input" /></Field>
            <Field label={tAdmin.impactMap.fPublished}>
              <select value={form.published ? "1" : "0"} onChange={e => setForm({...form, published: e.target.value === "1"})} className="form-input">
                <option value="1">{tAdmin.impactMap.yes}</option>
                <option value="0">{tAdmin.impactMap.no}</option>
              </select>
            </Field>
          </div>
          <div className="flex gap-2 mt-5">
            <button type="submit" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#002B49] text-white hover:bg-[#001F35] text-sm font-medium"><Save size={14} /> {editId ? tAdmin.impactMap.save : tAdmin.impactMap.create}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditId(null); setForm(emptyForm); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-black/10 text-[#64748B] text-sm"><X size={14} /> {tAdmin.impactMap.cancel}</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#64748B]">…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-[#64748B]">{tAdmin.impactMap.empty}</div>
        ) : (
          <ul className="divide-y divide-black/5">
            {items.map((c, i) => (
              <li key={c.id} className="p-5 flex items-center gap-4" data-testid={`country-row-${i}`}>
                <span className="text-2xl">{c.flag_emoji || "🌍"}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#002B49]">{c.name_fr}</span>
                    <span className="text-xs text-[#64748B]">· {c.region}</span>
                    {!c.published && <span className="text-[10px] uppercase tracking-wider text-[#64748B] bg-black/5 px-2 py-0.5 rounded-full">{tAdmin.impactMap.draft}</span>}
                  </div>
                  <div className="text-xs text-[#64748B] mt-1">{c.widows_supported} {tAdmin.impactMap.widows} · {c.orphans_supported} {tAdmin.impactMap.orphans} · {c.programs} {tAdmin.impactMap.programs}</div>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => togglePublish(c)} className="p-2 rounded-md hover:bg-black/5 text-[#64748B]" data-testid={`country-toggle-${i}`}>{c.published ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                  <button onClick={() => startEdit(c)} className="p-2 rounded-md hover:bg-black/5 text-[#64748B]" data-testid={`country-edit-${i}`}><Pencil size={14} /></button>
                  <button onClick={() => remove(c)} className="p-2 rounded-md hover:bg-red-50 text-red-600" data-testid={`country-delete-${i}`}><Trash2 size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <style>{`
        .form-input { width:100%; margin-top:.4rem; padding:.6rem .85rem; border-radius:.45rem; border:1px solid #E5E7EB; outline:none; transition:border-color .2s, box-shadow .2s; font-size:.9rem; color:#0A192F; background:#fff; }
        .form-input:focus { border-color:#002B49; box-shadow:0 0 0 3px rgba(0,43,73,.1); }
      `}</style>
    </div>
  );
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-[11px] font-medium text-[#64748B] uppercase tracking-wider">{label}</span>
    {children}
  </label>
);

export default ImpactMapManager;
